import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Mail, Loader, Check, LogOut, Ticket, CalendarDays,
  Sparkles, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/customer`;
const TOKEN_KEY = 'wc_customer_token';

// One-shot verify, initiated at module-load time (outside React) so that
// React 18 StrictMode unmount → remount cannot fire the request twice and
// burn the single-use magic-link token.
let _bootstrapVerifyPromise = null;
function _maybeStartBootstrapVerify() {
  if (_bootstrapVerifyPromise !== null) return _bootstrapVerifyPromise;
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');
  if (!token) {
    _bootstrapVerifyPromise = false; // sentinel: no token to verify
    return false;
  }
  _bootstrapVerifyPromise = axios
    .post(`${API}/auth/verify`, { token })
    .then((r) => r.data)
    .catch((err) => {
      // Wrap rejection so consumers see a normalized error shape.
      return Promise.reject(err);
    });
  return _bootstrapVerifyPromise;
}
// Kick off immediately when this module loads.
_maybeStartBootstrapVerify();

const PRIMARY = '#1f3d2b';

// ---------- atoms ----------
const Logo = () => (
  <img src="/logo.png" alt="Healthy Life Vitamins" className="h-12 mx-auto object-contain" />
);

const PrimaryButton = ({ children, onClick, disabled, dataTestid, type }) => (
  <button
    type={type || 'button'}
    data-testid={dataTestid}
    onClick={onClick}
    disabled={disabled}
    className="w-full rounded-2xl py-5 text-base sm:text-lg font-bold tracking-tight text-white shadow-[0_8px_30px_rgba(31,61,43,0.25)] active:scale-[0.98] transition-all disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed"
    style={{ backgroundColor: disabled ? undefined : PRIMARY, fontFamily: "'Manrope', sans-serif", minHeight: 60 }}
  >
    {children}
  </button>
);

const StageWrap = ({ children, k }) => (
  <motion.div
    key={k}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
    className="flex-1 flex flex-col w-full max-w-md mx-auto"
  >
    {children}
  </motion.div>
);

// ---------- 1. Sign-in (email entry) ----------
const SignIn = ({ onSent }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const canSubmit = /^[^@]+@[^@]+\.[^@]+$/.test(email.trim()) && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/request-link`, {
        email: email.trim(),
        origin_url: window.location.origin,
      });
      onSent(email.trim());
    } catch (err) {
      setError('We had trouble sending the link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StageWrap k="signin">
      <div className="pt-10 pb-6"><Logo /></div>
      <div className="flex-1 flex flex-col items-center text-center px-5">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
          style={{ backgroundColor: `${PRIMARY}10`, color: PRIMARY }}
        >
          <Mail size={28} strokeWidth={1.5} />
        </div>
        <h1
          className="text-4xl sm:text-5xl text-[#0d1f15] mb-3 leading-[1.05] tracking-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Wellness Club Member
        </h1>
        <p
          className="text-base text-[#5a7062] mb-8 max-w-sm"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Sign in to view your session credits, upcoming bookings, and book your next visit.
        </p>

        <form onSubmit={handleSubmit} className="w-full" data-testid="signin-form">
          <label
            htmlFor="account-email"
            className="block text-[10px] uppercase tracking-[0.25em] font-bold text-[#5a7062] text-left mb-2"
          >
            Email address
          </label>
          <input
            id="account-email"
            data-testid="signin-email-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full rounded-2xl border border-[#1f3d2b]/15 bg-white px-5 py-4 text-base text-[#0d1f15] focus:outline-none focus:border-[#1f3d2b]/60 transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          />
          {error && (
            <div
              data-testid="signin-error"
              className="mt-3 flex items-start gap-2 text-sm text-[#a33] text-left"
            >
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="mt-6">
            <PrimaryButton
              type="submit"
              disabled={!canSubmit}
              dataTestid="signin-submit-button"
            >
              {loading ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader size={18} className="animate-spin" /> Sending…
                </span>
              ) : 'Send me a sign-in link'}
            </PrimaryButton>
          </div>
        </form>

        <p
          className="text-[11px] text-[#7a8c81] mt-6 leading-relaxed"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          We&apos;ll email you a secure link that expires in 15 minutes. Your account is
          created automatically when you purchase a session or pack.
        </p>
      </div>
    </StageWrap>
  );
};

// ---------- 2. Link-sent confirmation ----------
const LinkSent = ({ email, onBack }) => (
  <StageWrap k="sent">
    <div className="pt-10 pb-6"><Logo /></div>
    <div className="flex-1 flex flex-col items-center text-center px-5">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: `${PRIMARY}15`, color: PRIMARY }}
      >
        <Check size={36} strokeWidth={2.5} />
      </div>
      <h1
        className="text-4xl text-[#0d1f15] mb-3 leading-tight"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
      >
        Check your email
      </h1>
      <p
        className="text-base text-[#5a7062] mb-2 max-w-sm"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        If <span className="font-semibold text-[#0d1f15]">{email}</span> matches a
        Wellness Club account, a sign-in link is on its way.
      </p>
      <p
        className="text-sm text-[#7a8c81] mb-10 max-w-sm"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        The link expires in 15 minutes. Don&apos;t forget to check your spam folder.
      </p>

      <button
        data-testid="signin-back-button"
        onClick={onBack}
        className="text-sm font-semibold text-[#1f3d2b] underline-offset-4 hover:underline inline-flex items-center gap-2"
      >
        <ArrowLeft size={16} /> Use a different email
      </button>
    </div>
  </StageWrap>
);

// ---------- 3. Verifying spinner (token in URL) ----------
const Verifying = () => (
  <StageWrap k="verifying">
    <div className="flex-1 flex flex-col items-center justify-center text-center px-5">
      <Loader size={36} className="animate-spin text-[#1f3d2b] mb-4" />
      <p className="text-base text-[#5a7062]" style={{ fontFamily: "'Manrope', sans-serif" }}>
        Signing you in…
      </p>
    </div>
  </StageWrap>
);

// ---------- 4. Dashboard ----------
const formatDate = (iso) => {
  if (!iso) return '';
  const d = iso.length > 10 ? new Date(iso) : new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = Math.ceil((d - new Date()) / 86400000);
  return diff;
};

const Dashboard = ({ data, onLogout, onReload }) => {
  const { customer, packs, bookings } = data;
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.booking_date >= todayIso);
  const past = bookings.filter((b) => b.booking_date < todayIso);
  const activePack = packs[0];

  return (
    <StageWrap k="dashboard">
      <div className="pt-8 pb-3 px-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="" className="h-9 object-contain" />
        </div>
        <button
          data-testid="account-logout-button"
          onClick={onLogout}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5a7062] hover:text-[#0d1f15] px-3 py-2 rounded-lg transition-colors"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <div className="px-5">
        <p
          className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#5a7062] mb-1"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Welcome back
        </p>
        <h1
          className="text-4xl text-[#0d1f15] leading-tight mb-6 tracking-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          {customer.first_name || customer.email.split('@')[0]}
        </h1>

        {/* Pack / Membership card */}
        {activePack ? (
          <div
            data-testid="account-pack-card"
            className="rounded-3xl p-6 mb-5 text-white shadow-[0_12px_40px_rgba(31,61,43,0.25)]"
            style={{ background: 'linear-gradient(135deg, #1f3d2b 0%, #2d5a3f 100%)' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Ticket size={16} />
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-80">
                {activePack.type === 'membership' ? 'Wellness Membership' : 'Wellness Pack'}
              </span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span
                className="text-6xl leading-none"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                data-testid="account-sessions-remaining"
              >
                {activePack.sessions_remaining}
              </span>
              <span className="text-sm opacity-80">
                of {activePack.total_sessions} sessions
              </span>
            </div>
            <div
              className="flex items-center gap-2 text-xs opacity-80 pt-3 border-t border-white/20"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <CalendarDays size={13} />
              <span data-testid="account-pack-expiry">
                {activePack.type === 'membership' && activePack.renews_at ? (
                  <>Renews {formatDate(activePack.renews_at)}</>
                ) : (
                  <>Expires {formatDate(activePack.expires_at)}</>
                )}
                {daysUntil(activePack.type === 'membership' && activePack.renews_at ? activePack.renews_at : activePack.expires_at) !== null && daysUntil(activePack.type === 'membership' && activePack.renews_at ? activePack.renews_at : activePack.expires_at) >= 0 && (
                  <> · {daysUntil(activePack.type === 'membership' && activePack.renews_at ? activePack.renews_at : activePack.expires_at)} days</>
                )}
              </span>
            </div>
          </div>
        ) : (
          <div
            data-testid="account-no-pack-card"
            className="rounded-3xl bg-white border-2 border-dashed border-[#1f3d2b]/20 p-6 mb-5 text-center"
          >
            <Sparkles size={22} className="mx-auto mb-2 text-[#d97736]" />
            <p
              className="text-sm text-[#5a7062] mb-3"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              No active pack credit. Save $36 with a 4-session pack.
            </p>
            <a
              href="/booking"
              data-testid="account-buy-pack-link"
              className="inline-block text-sm font-bold text-[#1f3d2b] underline underline-offset-4"
            >
              View Wellness Packs →
            </a>
          </div>
        )}

        {/* CTAs */}
        <div className="grid grid-cols-1 gap-3 mb-8">
          <a
            href="/booking"
            data-testid="account-book-session-cta"
            className="w-full rounded-2xl py-4 text-center text-base font-bold text-white shadow-[0_6px_20px_rgba(31,61,43,0.2)] active:scale-[0.98] transition-all"
            style={{ backgroundColor: PRIMARY, fontFamily: "'Manrope', sans-serif" }}
          >
            {activePack && activePack.sessions_remaining > 0 ? 'Book with a credit' : 'Book a session'}
          </a>
          {activePack && (
            <a
              href="/booking"
              data-testid="account-buy-another-pack-link"
              className="w-full rounded-2xl py-3 text-center text-sm font-bold text-[#1f3d2b] border-2 border-[#1f3d2b]/15 hover:border-[#1f3d2b]/40 transition-all"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Buy another pack
            </a>
          )}
        </div>

        {/* Upcoming bookings */}
        <section className="mb-8">
          <h2
            className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#5a7062] mb-3"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Upcoming sessions
          </h2>
          {upcoming.length === 0 ? (
            <div
              className="rounded-2xl bg-white border border-[#1f3d2b]/10 p-4 text-sm text-[#5a7062] text-center"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="account-no-upcoming"
            >
              No upcoming sessions on the calendar.
            </div>
          ) : (
            <div className="space-y-2">
              {upcoming.map((b) => (
                <div
                  key={b.id}
                  data-testid={`account-upcoming-${b.id}`}
                  className="rounded-2xl bg-white border border-[#1f3d2b]/10 p-4 flex items-center justify-between"
                >
                  <div>
                    <div
                      className="text-base font-bold text-[#0d1f15]"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {formatDate(b.booking_date)}
                    </div>
                    <div
                      className="text-sm text-[#5a7062]"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {b.slot_label || b.slot}
                    </div>
                  </div>
                  {b.redeemed_pack_id && (
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#d97736] bg-[#d97736]/10 px-2 py-1 rounded-full">
                      Pack
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Past bookings */}
        {past.length > 0 && (
          <section className="mb-10">
            <h2
              className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#5a7062] mb-3"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              History
            </h2>
            <div className="space-y-2">
              {past.slice(0, 8).map((b) => (
                <div
                  key={b.id}
                  data-testid={`account-past-${b.id}`}
                  className="rounded-xl bg-transparent border border-[#1f3d2b]/8 p-3 flex items-center justify-between opacity-70"
                >
                  <span
                    className="text-sm text-[#0d1f15]"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {formatDate(b.booking_date)} · {b.slot_label || b.slot}
                  </span>
                  {b.redeemed_pack_id && (
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#d97736]">
                      Pack
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </StageWrap>
  );
};

// ---------- 5. Error stage (bad/expired token) ----------
const VerifyError = ({ error, onRetry }) => (
  <StageWrap k="verify-error">
    <div className="pt-10 pb-6"><Logo /></div>
    <div className="flex-1 flex flex-col items-center text-center px-5">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
        style={{ backgroundColor: '#a3333310', color: '#a33' }}
      >
        <AlertCircle size={28} strokeWidth={1.5} />
      </div>
      <h1
        className="text-3xl text-[#0d1f15] mb-3 leading-tight"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
      >
        Link not working
      </h1>
      <p
        className="text-base text-[#5a7062] mb-8 max-w-sm"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        {error || 'This sign-in link is invalid or has expired.'}
      </p>
      <button
        data-testid="verify-retry-button"
        onClick={onRetry}
        className="w-full max-w-xs rounded-2xl py-4 text-base font-bold text-white"
        style={{ backgroundColor: PRIMARY, fontFamily: "'Manrope', sans-serif" }}
      >
        Request a new link
      </button>
    </div>
  </StageWrap>
);

// ---------- Root ----------
const AccountApp = () => {
  // stages: 'loading' | 'signin' | 'sent' | 'verifying' | 'dashboard' | 'error'
  const [stage, setStage] = useState('loading');
  const [sentEmail, setSentEmail] = useState('');
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const loadProfile = useCallback(async (jwt) => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      setData(data);
      setStage('dashboard');
    } catch (err) {
      localStorage.removeItem(TOKEN_KEY);
      setStage('signin');
    }
  }, []);

  // On mount: pick up the bootstrap verify promise (started at module load)
  // if a ?token= was present, otherwise check localStorage.
  useEffect(() => {
    const bootstrap = _bootstrapVerifyPromise;
    if (bootstrap && bootstrap !== false) {
      setStage('verifying');
      bootstrap
        .then(async (data) => {
          localStorage.setItem(TOKEN_KEY, data.token);
          const u = new URL(window.location.href);
          u.searchParams.delete('token');
          window.history.replaceState({}, '', u.pathname + (u.search || ''));
          await loadProfile(data.token);
        })
        .catch((err) => {
          setError(err?.response?.data?.detail || 'This sign-in link is invalid or has expired.');
          setStage('error');
        });
      return;
    }
    const existing = localStorage.getItem(TOKEN_KEY);
    if (existing) {
      loadProfile(existing);
    } else {
      setStage('signin');
    }
  }, [loadProfile]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setData(null);
    setStage('signin');
  };

  return (
    <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: '#f6f4ef' }}>
      {/* Persistent home link */}
      <a
        href="/"
        data-testid="account-home-link"
        className="absolute top-4 left-4 z-20 inline-flex items-center gap-1 transition-opacity hover:opacity-100 opacity-50"
        style={{ color: '#1f3d2b', fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}
      >
        <ArrowLeft size={12} strokeWidth={2.5} /> Home
      </a>
      <AnimatePresence mode="wait">
        {stage === 'loading' && <Verifying key="loading" />}
        {stage === 'signin' && (
          <SignIn
            key="signin"
            onSent={(email) => { setSentEmail(email); setStage('sent'); }}
          />
        )}
        {stage === 'sent' && (
          <LinkSent
            key="sent"
            email={sentEmail}
            onBack={() => setStage('signin')}
          />
        )}
        {stage === 'verifying' && <Verifying key="verifying" />}
        {stage === 'dashboard' && data && (
          <Dashboard
            key="dashboard"
            data={data}
            onLogout={handleLogout}
            onReload={() => loadProfile(localStorage.getItem(TOKEN_KEY))}
          />
        )}
        {stage === 'error' && (
          <VerifyError
            key="error"
            error={error}
            onRetry={() => { setError(''); setStage('signin'); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountApp;
